import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Briefcase, ArrowRight, X } from 'lucide-react';

interface WaitlistTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectResident: () => void;
  onSelectVendor: () => void;
  cityName: string;
}

export function WaitlistTypeDialog({
  open,
  onOpenChange,
  onSelectResident,
  onSelectVendor,
  cityName,
}: WaitlistTypeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <DialogHeader>
          <DialogTitle className="text-2xl sm:text-3xl text-center">
            Choose Your Waitlist
          </DialogTitle>
          <DialogDescription className="text-center text-base sm:text-lg mt-2">
            Select the waitlist that best describes you
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4">
          {/* Resident Waitlist Option */}
          <Card
            className="border-2 hover:border-primary/50 transition-colors cursor-pointer group"
            onClick={onSelectResident}
          >
            <CardContent className="pt-6 pb-6 text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors">
                <Users className="w-8 h-8 text-primary" />
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Resident Waitlist</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Join as a {cityName} community member. Get early access to group buying, verified
                  vendors, and community organizing tools.
                </p>
              </div>

              <Button
                className="w-full group-hover:shadow-md transition-shadow"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectResident();
                }}
              >
                Join as Resident
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="pt-3 border-t space-y-1 text-xs text-muted-foreground">
                <p>✓ Group buying power</p>
                <p>✓ Verified marketplace access</p>
                <p>✓ Community governance tools</p>
              </div>
            </CardContent>
          </Card>

          {/* Vendor Waitlist Option */}
          <Card
            className="border-2 hover:border-primary/50 transition-colors cursor-pointer group"
            onClick={onSelectVendor}
          >
            <CardContent className="pt-6 pb-6 text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto group-hover:bg-primary/20 transition-colors">
                <Briefcase className="w-8 h-8 text-primary" />
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Vendor Waitlist</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Join as a service provider. Access verified customers, bid on community contracts,
                  and grow your business at scale.
                </p>
              </div>

              <Button
                className="w-full group-hover:shadow-md transition-shadow"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectVendor();
                }}
              >
                Join as Vendor
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <div className="pt-3 border-t space-y-1 text-xs text-muted-foreground">
                <p>✓ Enterprise-grade verification</p>
                <p>✓ Large-scale contract bidding</p>
                <p>✓ 2,000+ verified homes network</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-center text-muted-foreground mt-4">
          Not sure which one? Residents live in {cityName} communities. Vendors provide services to
          them.
        </p>
      </DialogContent>
    </Dialog>
  );
}
